import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import Colours from '@shared/ui/atoms/Colours';
import Icon, { IconTypes } from '@shared/ui/atoms/icon/Icon';
import { BodySmall } from '@shared/ui/atoms/fonts/Body';
import { ButtonSize } from '@shared/ui/atoms/buttons/ButtonStyling';
import { OutlineButton } from '@shared/ui/atoms/buttons/OutlineButton';
import { Locker } from '../data/lockers.models';
import { RowAction } from './LockerViewComponents';

/**
 * Assigning from the queue is a two-part decision — this person, that locker — so the free lockers
 * hang off the person's row rather than sending an admin back to the directory to find one.
 */
export function FreeLockerMenu(props: Props) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);

  const onPick = (locker: Locker) => {
    setIsOpen(false);
    props.onSelect(locker);
  };

  return (
    <MenuAnchor>
      <RowAction width={170}>
        <OutlineButton text={'lockers.assign-a-locker'} icon={IconTypes.Down} size={ButtonSize.Small}
                       disabled={props.disabled || props.freeLockers.length === 0}
                       click={() => setIsOpen(!isOpen)} />
      </RowAction>

      {isOpen && (
        <>
          <MenuBackdrop onClick={() => setIsOpen(false)} />
          <MenuPopover>
            {props.freeLockers.map((locker: Locker) => (
              <MenuItem key={locker.id} type={'button'} onClick={() => onPick(locker)}>
                <Icon icon={IconTypes.Padlock} style={{ color: Colours.mildGrey }} />
                <BodySmall>{locker.label}</BodySmall>
              </MenuItem>
            ))}
          </MenuPopover>
        </>
      )}
    </MenuAnchor>
  );
}

interface Props {
  freeLockers: Locker[];
  disabled?: boolean;
  onSelect: (locker: Locker) => void;
}

const MenuAnchor = styled.div`
  position: relative;
`

const MenuBackdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: 10;
`

const MenuPopover = styled.div`
  position: absolute;
  top: calc(100% + 4px);
  right: 0;
  z-index: 11;
  min-width: 180px;
  max-height: 280px;
  overflow-y: auto;
  background-color: white;
  border: 1px solid ${Colours.lightGrey};
  border-radius: 10px;
  padding: 4px;
  box-shadow: 0 4px 16px rgba(8, 30, 34, 0.12);
`

const MenuItem = styled.button`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 10px 12px;
  border: none;
  border-radius: 6px;
  background-color: white;
  cursor: pointer;
  text-align: left;

  &:hover {
    background-color: ${Colours.veryLightGrey};
  }
`
